import React, { useState, useMemo } from "react";
import classNames from "classnames";
import { FcBullish } from "react-icons/fc";
import { HiOutlineLogout, HiChevronDown, HiX } from "react-icons/hi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DASHBOARD_SIDEBAR_BOTTOM_LINKS,
  ANALYTICS_SIDEBAR_LINKS,
  OFFER_MANAGEMENT_SIDEBAR_LINKS,
  PRODUCT_MANAGEMENT_SIDEBAR_LINKS,
  CONTENT_MANAGEMENT_SIDEBAR_LINKS,
  USER_MANAGEMENT_SIDEBAR_LINKS,
  POST_AND_VALUE_TREND_LINKS,
  KYC_SIDEBAR_LINKS,
  // NOTE: Notification Manager commented out - not in current project scope
  // NOTIFICATION_MANAGEMENT_SIDEBAR_LINKS,
} from "../../lib/consts/navigation";

const linkClass =
  "flex items-center gap-2 font-light px-3 py-2 hover:bg-neutral-700 hover:no-underline active:bg-neutral-600 rounded-sm text-base";

function Sidebar({ isOpen = true, onClose }) {
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [offerManagementOpen, setOfferManagementOpen] = useState(false);
  const [productManagementOpen, setProductManagementOpen] = useState(false);
  const [PostTrendOpen, setPostTrendOpen] = useState(false);
  const [contentManagementOpen, setContentManagementOpen] = useState(false);
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  // NOTE: Notification Manager commented out - not in current project scope
  // const [notificationManagementOpen, setNotificationManagementOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Parse stored admin info once
  const adminInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminInfo") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin = adminInfo.role === "superadmin";
  const isAdmin = adminInfo.role === "admin";
  const isGuest = adminInfo.role === "subadmin";
  const canAccessKYC = isSuperAdmin || isAdmin;

  const handleLogout = () => {
    // Delete auth cookie
    document.cookie = "token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT;";
    // Clear stored admin data
    localStorage.removeItem("userRole");
    localStorage.removeItem("adminInfo");
    // Redirect
    navigate("/login", { replace: true });
  };

  return (
    <>
      <div
        className={classNames(
          "bg-neutral-900 w-60 p-3 flex flex-col text-white h-screen fixed lg:static z-50 transition-transform duration-300 ease-in-out",
          {
            "-translate-x-full lg:translate-x-0": !isOpen,
            "translate-x-0": isOpen,
          }
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-1 py-3">
          <div className="flex items-center gap-2">
            <FcBullish fontSize={24} />
            <span className="text-neutral text-lg">Dashboard</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md hover:bg-neutral-700 transition-colors"
            aria-label="Close sidebar"
          >
            <HiX className="text-xl" />
          </button>
        </div>

      {/* Main Section with Dropdown Groups */}
      <div className="flex-1 overflow-y-auto py-4">
        {/* Always show Analytics */}
        {isGuest && (
          <>
            {" "}
            <Section
              title="Analytics"
              isOpen={analyticsOpen}
              setIsOpen={setAnalyticsOpen}
              links={ANALYTICS_SIDEBAR_LINKS}
              onClose={onClose}
            />
            <Section
              title="Post & Value Trends"
              isOpen={PostTrendOpen}
              setIsOpen={setPostTrendOpen}
              links={POST_AND_VALUE_TREND_LINKS}
              className="mt-4"
              onClose={onClose}
            />
          </>
        )}
        {/* Other sections only visible if not guest */}
        {!isGuest && (
          <>
            <Section
              title="Analytics"
              isOpen={analyticsOpen}
              setIsOpen={setAnalyticsOpen}
              links={ANALYTICS_SIDEBAR_LINKS}
              onClose={onClose}
            />
            <Section
              title="Offer Management"
              isOpen={offerManagementOpen}
              setIsOpen={setOfferManagementOpen}
              links={OFFER_MANAGEMENT_SIDEBAR_LINKS}
              className="mt-4"
              onClose={onClose}
            />

            <Section
              title="Content Management"
              isOpen={contentManagementOpen}
              setIsOpen={setContentManagementOpen}
              links={CONTENT_MANAGEMENT_SIDEBAR_LINKS}
              className="mt-4"
              onClose={onClose}
            />

            <Section
              title="Product Management"
              isOpen={productManagementOpen}
              setIsOpen={setProductManagementOpen}
              links={PRODUCT_MANAGEMENT_SIDEBAR_LINKS}
              className="mt-4"
              onClose={onClose}
            />

            <Section
              title="Post & Value Trends"
              isOpen={PostTrendOpen}
              setIsOpen={setPostTrendOpen}
              links={POST_AND_VALUE_TREND_LINKS}
              className="mt-4"
              onClose={onClose}
            />

            {canAccessKYC && (
              <Section
                title="KYC"
                isOpen={kycOpen}
                setIsOpen={setKycOpen}
                links={KYC_SIDEBAR_LINKS}
                className="mt-4"
                onClose={onClose}
              />
            )}

            {/* NOTE: Notification Manager commented out - not in current project scope */}
            {/* {canAccessKYC && (
              <Section
                title="Notification Management"
                isOpen={notificationManagementOpen}
                setIsOpen={setNotificationManagementOpen}
                links={NOTIFICATION_MANAGEMENT_SIDEBAR_LINKS}
                className="mt-4"
                onClose={onClose}
              />
            )} */}

            {isSuperAdmin && (
              <Section
                title="User Management"
                isOpen={userManagementOpen}
                setIsOpen={setUserManagementOpen}
                links={USER_MANAGEMENT_SIDEBAR_LINKS}
                className="mt-4"
                onClose={onClose}
              />
            )}
          </>
        )}
      </div>

      {/* Bottom Links & Logout */}
      <div className="flex flex-col gap-0.5 pt-2 border-t border-neutral-700">
        {DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((item) => (
          <SidebarLink key={item.key} item={item} pathname={pathname} onClose={onClose} />
        ))}

        <div
          onClick={handleLogout}
          className={classNames("text-red-500 cursor-pointer", linkClass)}
        >
          <span className="text-xl">
            <HiOutlineLogout />
          </span>
          Logout
        </div>
      </div>
      </div>
    </>
  );
}

export default Sidebar;

// Helper for dropdown sections
function Section({ title, isOpen, setIsOpen, links, className = "", onClose }) {
  return (
    <>
      <div
        className={classNames(
          "py-2 cursor-pointer flex items-center justify-between text-white font-bold text-base",
          className
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{title}</span>
        <HiChevronDown
          className={classNames("transform transition-transform duration-300", {
            "rotate-180": isOpen,
          })}
        />
      </div>
      {isOpen && (
        <div className="flex flex-col gap-0.5">
          {links.map((item) => (
            <SidebarLink
              key={item.key}
              item={item}
              pathname={window.location.pathname}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Individual link
function SidebarLink({ item, pathname, onClose }) {
  let linkPath = item.path;
  if (item.dynamic && item.params) {
    linkPath = `${item.path}/${item.params.startDate}/${item.params.endDate}`;
  }

  const handleClick = () => {
    // Close sidebar on mobile when link is clicked
    if (onClose && window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <Link
      to={linkPath}
      onClick={handleClick}
      className={classNames(
        pathname === item.path
          ? "bg-neutral-700 text-white"
          : "text-neutral-400",
        linkClass
      )}
    >
      <span className="text-xl">{item.icon}</span>
      {item.label}
    </Link>
  );
}
